import { useRef, useState } from 'react'
import type { PackagingMode, Tag, TagGroupDef } from '@shared/types'
import { useTagRows } from '@shared/hooks/useTagRows'
import './filterBar.css'

interface FilterBarProps {
  tags: Tag[]
  groups: TagGroupDef[]
  activeTagIds: string[]
  onToggleTag: (tagId: string) => void
  /** Leave both out and the packaging buttons are not shown: they are Audit's. */
  packaging?: PackagingMode
  onPackagingChange?: (mode: PackagingMode) => void
  /**
   * One row of every tag, with no categories: Visual Language. The page
   * decides what a click does (there, one tag at a time).
   */
  flat?: boolean
  hasFilters: boolean
  onClear: () => void
  /**
   * Holding the Filters button this long calls it: how an administrator
   * brings up the hidden colour tuner. Left out for everyone else, and then
   * a long press is just a click.
   */
  onLongPress?: () => void
  /**
   * Categories not filled in for every picture yet (libraries.ts,
   * unfinishedGroups): the row reads at half strength, its name with an
   * asterisk, so a missing tag is not taken for a picture not being that.
   */
  unfinished?: string[]
}

const LONG_PRESS_MS = 5000

/**
 * A hue for each category's tags, in order (Huanrui, 2026-10-02): a faint
 * tint behind the tags, so the categories read apart at a glance. Spread
 * round the wheel so neighbours never look alike. OKLCH hues (web.css).
 */
const CATEGORY_HUES = [255, 65, 305, 15, 155, 125, 205, 345, 90, 280, 180, 40, 325]

const PACKAGING_OPTIONS: { value: PackagingMode; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'packaging', label: 'Packaging only' },
  { value: 'non-packaging', label: 'Non-packaging only' },
]

/**
 * The filter, across the top of the library.
 *
 * One row per category, its tags beside it, and Audit's packaging buttons
 * below them -- all folded away by default, so the pictures start at the top
 * of the page (Huanrui, 2026-10-02). Visual Language opens with its tags
 * showing: one row of them is the whole of its filter. The badge on the
 * folded control counts the packaging choice too, so it is never a mystery
 * that something is narrowing the pictures.
 */
export function FilterBar({
  tags,
  groups,
  activeTagIds,
  onToggleTag,
  packaging,
  onPackagingChange,
  unfinished = [],
  flat = false,
  hasFilters,
  onClear,
  onLongPress,
}: FilterBarProps) {
  const [open, setOpen] = useState(flat)
  const pressTimer = useRef<number | null>(null)
  const pressFired = useRef(false)
  const endPress = () => {
    if (pressTimer.current !== null) window.clearTimeout(pressTimer.current)
    pressTimer.current = null
  }
  const startPress = () => {
    if (!onLongPress) return
    endPress()
    pressFired.current = false
    pressTimer.current = window.setTimeout(() => {
      pressTimer.current = null
      pressFired.current = true // the click that ends this press is not a toggle
      setOpen(true)
      onLongPress()
    }, LONG_PRESS_MS)
  }
  const narrowed = activeTagIds.length + (packaging && packaging !== 'all' ? 1 : 0)

  const grouped = useTagRows(tags, activeTagIds, groups)
  // Flat: every tag in one row, most used first, with no category name --
  // Visual Language's tags have no categories, and its one hidden category
  // would otherwise hide them all.
  const rows = flat
    ? [
        {
          group: 'all',
          label: '',
          items: [...tags].sort(
            (a, b) => (b.assetCount ?? 0) - (a.assetCount ?? 0) || a.name.localeCompare(b.name),
          ),
        },
      ]
    : grouped

  return (
    <section className={`web-filter${open ? ' web-filter--open' : ''}`} aria-label="Filters">
      {/* Visual Language has no Filters button: its row of tags is always
          there (Huanrui, 2026-10-02). */}
      <div className="web-filter__head">
        {flat ? null : (
        <button
          type="button"
          className="web-filter__toggle"
          aria-expanded={open}
          onPointerDown={startPress}
          onPointerUp={endPress}
          onPointerLeave={endPress}
          onPointerCancel={endPress}
          onClick={() => {
            if (pressFired.current) {
              pressFired.current = false
              return
            }
            setOpen((value) => !value)
          }}
        >
          <span className={`web-filter__caret${open ? ' web-filter__caret--open' : ''}`} aria-hidden="true">
            ›
          </span>
          Filters
          {narrowed > 0 ? <span className="web-filter__badge">{narrowed}</span> : null}
        </button>
        )}

        {hasFilters ? (
          <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={onClear}>
            Clear filters
          </button>
        ) : null}
      </div>

      {flat ? (
        // Visual Language: its one row of tags, always there, larger.
        <div className="wf-tags wf-tags--flat">
          {rows[0]?.items.map((tag) => (
            <TagButton key={tag.id} tag={tag} active={activeTagIds.includes(tag.id)} onToggle={onToggleTag} />
          ))}
        </div>
      ) : (
        /*
         * The opened filter, as one sheet (2026-10-02, Huanrui: "make it look
         * considered"). A quiet index rather than a wall of outlined pills:
         * each category a row with its name in small capitals and a dot of
         * its colour; its tags softly washed in that colour, no outlines; the
         * chosen ones filled. Hairlines between rows. Categories with no tags
         * yet are left out here -- Tag Manager is where they are filled.
         *
         * Always rendered and folded by CSS, so it opens and closes with a
         * slide instead of popping in; `inert` keeps the folded tags out of
         * reach of the keyboard.
         */
        <div
          className={`wf-fold${open ? ' wf-fold--open' : ''}`}
          {...(open ? {} : { inert: '' })}
        >
        <div className="wf-fold__inner">
        <div className="wf-panel">
          {packaging && onPackagingChange ? (
            <div className="wf-row wf-row--neutral">
              <span className="wf-label">
                <i className="wf-dot" aria-hidden="true" />
                Packaging
              </span>
              <div className="wf-tags" role="group" aria-label="Packaging">
                {PACKAGING_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`wf-tag${packaging === option.value ? ' wf-tag--on' : ''}`}
                    aria-pressed={packaging === option.value}
                    onClick={() => onPackagingChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {rows
            .map((row, index) => ({ ...row, hue: CATEGORY_HUES[index % CATEGORY_HUES.length] }))
            .filter((row) => row.items.length > 0)
            .map(({ group, label, items, hue }) => (
              <div
                className={`wf-row${unfinished.includes(group) ? ' wf-row--unfinished' : ''}`}
                key={group}
                style={{ '--tag-hue': hue } as React.CSSProperties}
                title={unfinished.includes(group) ? 'Not filled in for every picture yet' : undefined}
              >
                <span className="wf-label">
                  <i className="wf-dot" aria-hidden="true" />
                  {label}
                  {unfinished.includes(group) ? <span className="wf-unfinished" aria-label="not finished">*</span> : null}
                </span>
                <div className="wf-tags">
                  {items.map((tag) => (
                    <TagButton key={tag.id} tag={tag} active={activeTagIds.includes(tag.id)} onToggle={onToggleTag} />
                  ))}
                </div>
              </div>
            ))}
        </div>
        </div>
        </div>
      )}
    </section>
  )
}

function TagButton({ tag, active, onToggle }: { tag: Tag; active: boolean; onToggle: (id: string) => void }) {
  return (
    <button
      type="button"
      className={`wf-tag${active ? ' wf-tag--on' : ''}`}
      aria-pressed={active}
      onClick={() => onToggle(tag.id)}
    >
      {tag.name}
      {typeof tag.assetCount === 'number' ? <span className="wf-count">{tag.assetCount}</span> : null}
    </button>
  )
}
